import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Chip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CheckIcon from "@mui/icons-material/Check";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSaveBanquetBookingMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  INITIAL_FORM,
  BookingFormState,
  computeFinancials,
  computeLEDTotal,
  n,
  fmtCAD,
} from "./bookingTypes";
import { TIME_SLOTS } from "../Pricing/constants";
import {
  calculatePricing,
  type DiscountResult,
} from "./pricingEngine";
import Step1EventDetails from "./Step1EventDetails";
import Step2Services from "./Step2Services";
import Step3Addons from "./Step3Addons";
import BookingSummary from "./BookingSummary";

/* ═══════════════════════════════════════════════════════════════════════════
   TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy:    "#0f172a",
  navyMid: "#1e293b",
  gold:    "#d4a373",
  cream:   "#f8f5f0",
  white:   "#ffffff",
  bg:      "#f8f5f0",
  slate:   "#6b7280",
  slateLt: "#9ca3af",
  line:    "#e5e7eb",
  doneBg:  "#f8f5f0",
  doneTx:  "#6b7280",
  doneBd:  "#d4a373",
  redTx:   "#9b2c2c",
  peach:   "#f8f5f0",
  inputBg: "#fafaf9",
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
   STEPS
   ═══════════════════════════════════════════════════════════════════════════ */
const STEPS = [
  { label: "Event & Customer", short: "Event", icon: <EventOutlinedIcon sx={{ fontSize: 17 }} /> },
  { label: "Pricing & Services", short: "Pricing", icon: <AttachMoneyIcon sx={{ fontSize: 17 }} /> },
  { label: "Add-ons", short: "Add-ons", icon: <ExtensionOutlinedIcon sx={{ fontSize: 17 }} /> },
];

/* ═══════════════════════════════════════════════════════════════════════════
   VALIDATION
   ═══════════════════════════════════════════════════════════════════════════ */
interface StepValidation { valid: boolean; errors: string[] }
const validateStep1 = (f: BookingFormState): StepValidation => {
  const e: string[] = [];
  if (!f.eventType) e.push("Event type is required");
  if (!f.expectedGuests || Number(f.expectedGuests) <= 0) e.push("Number of guests is required");
  if (Number(f.expectedGuests) > 900) e.push("Maximum capacity is 900 guests");
  if (!f.eventDate) e.push("Event date is required");
  if (!f.startTime) e.push("Start time is required");
  if (!f.endTime) e.push("End time is required");
  if (f.halls.length === 0) e.push("Select at least one hall");
  // Phone validation (10 digits)
  const phoneDigits = f.primaryCustomer.phone1.replace(/\D/g, "");
  if (phoneDigits.length > 0 && phoneDigits.length < 10) e.push("Enter a valid 10-digit phone number");
  // Organization required for corporate event types
  const corporateTypes = ["Corporate Event", "Conference", "Seminar", "Workshop"];
  if (corporateTypes.includes(f.eventType) && !f.primaryCustomer.organization) e.push("Organization is required for corporate events");
  return { valid: e.length === 0, errors: e };
};
const validateStep2 = (f: BookingFormState): StepValidation => {
  const e: string[] = [];
  if (n(f.guestCount) < 1) e.push("Guest count must be at least 1");
  return { valid: e.length === 0, errors: e };
};
const validateStep3 = (): StepValidation => ({ valid: true, errors: [] });
const validators = [validateStep1, validateStep2, validateStep3];

/* ═══════════════════════════════════════════════════════════════════════════
   LIVE CALCULATION
   ═══════════════════════════════════════════════════════════════════════════ */
interface Totals {
  hallSubtotal: number; foodSubtotal: number; barSubtotal: number;
  addonsSubtotal: number; vendorServicesSubtotal: number; ledSubtotal: number; baseSubtotal: number;
  adjustment: number; serviceChargeAmount: number; gratuityAmount: number;
  subtotal: number; hstAmount: number; grandTotal: number; guests: number;
  depositsPaid: number; balanceDue: number;
  // Pricing engine breakdown
  discountResult: DiscountResult | null;
  discountApplied: number;
}
function calculateTotal(form: BookingFormState): Totals {
  const fin = computeFinancials(form);
  const addonsSubtotal = form.addons.reduce((s, a) => s + a.lineTotal, 0);
  const vendorServicesSubtotal = form.vendorServices.reduce((s, vs) => s + vs.finalPrice, 0);
  const ledSubtotal = computeLEDTotal(form.selectedLEDs, form.halls);
  const baseSubtotal = fin.baseSubtotal + addonsSubtotal + vendorServicesSubtotal + ledSubtotal;
  const adjustment = n(form.adjustment);

  // Run pricing engine when there's a negative adjustment (= discount)
  let discountResult: DiscountResult | null = null;
  let discountApplied = 0;
  if (adjustment < 0) {
    discountResult = calculatePricing(form, Math.abs(adjustment));
    discountApplied = discountResult.discountApplied;
  }

  // If discount applied, use discounted total; otherwise normal flow
  const effectiveBase = discountResult
    ? discountResult.totalAfter
    : baseSubtotal + adjustment; // positive adjustment adds, 0 = no change
  const positiveAdjustment = adjustment > 0 ? adjustment : 0;
  const baseForCharges = discountResult ? discountResult.totalAfter : baseSubtotal;
  const serviceChargeAmount = baseForCharges * (n(form.serviceChargePercent) / 100);
  const gratuityAmount = baseForCharges * (n(form.gratuityPercent) / 100);
  const subtotal = effectiveBase + positiveAdjustment + serviceChargeAmount + gratuityAmount;
  const hstAmount = subtotal * 0.13;
  const grandTotal = subtotal + hstAmount;
  const depositsPaid = form.payments.reduce((s, p) => s + n(p.amount), 0) + n(form.securityDepositAmount);
  return {
    hallSubtotal: fin.hallSubtotal, foodSubtotal: fin.foodSubtotal, barSubtotal: fin.barSubtotal,
    addonsSubtotal, vendorServicesSubtotal, ledSubtotal, baseSubtotal,
    adjustment, serviceChargeAmount, gratuityAmount, subtotal, hstAmount, grandTotal,
    guests: n(form.expectedGuests) || n(form.guestCount), depositsPaid, balanceDue: grandTotal - depositsPaid,
    discountResult, discountApplied,
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   ANIMATED VALUE HOOK
   ═══════════════════════════════════════════════════════════════════════════ */
const useFlash = (value: number) => {
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) { setFlash(true); const t = setTimeout(() => setFlash(false), 350); prev.current = value; return () => clearTimeout(t); }
  }, [value]);
  return flash;
};

/* ═══════════════════════════════════════════════════════════════════════════
   STEPPER
   ═══════════════════════════════════════════════════════════════════════════ */
const Stepper: React.FC<{
  active: number; onClick: (i: number) => void;
  status: ("current" | "done" | "pending")[]; xs: boolean;
}> = ({ active, onClick, status, xs }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 3, md: 4 } }}>
    {STEPS.map((step, i) => {
      const s = status[i];
      return (
        <React.Fragment key={i}>
          {i > 0 && (
            <Box sx={{ flex: 1, height: 1, mx: { xs: 1, md: 2 }, bgcolor: s === "done" || status[i-1] === "done" ? T.doneBd : T.line, transition: "background 0.4s" }} />
          )}
          <Box onClick={() => onClick(i)} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover .step-num": { transform: "scale(1.08)" } }}>
            <Box className="step-num" sx={{
              width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
              bgcolor: s === "done" ? T.doneBg : s === "current" ? T.navy : T.bg,
              color: s === "done" ? T.doneTx : s === "current" ? T.gold : T.slateLt,
              border: `2px solid ${s === "done" ? T.doneBd : s === "current" ? T.navy : "transparent"}`,
              transition: "all 0.25s", flexShrink: 0,
              boxShadow: s === "current" ? "0 2px 8px rgba(13,27,42,0.15)" : "none",
            }}>
              {s === "done" ? <CheckIcon sx={{ fontSize: 16 }} /> : <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{i + 1}</Typography>}
            </Box>
            {!xs && (
              <Typography sx={{
                fontSize: 13, fontWeight: s === "current" ? 600 : 400,
                color: s === "current" ? T.navy : s === "done" ? T.doneTx : T.slateLt,
                transition: "color 0.2s",
              }}>
                {step.label}
              </Typography>
            )}
          </Box>
        </React.Fragment>
      );
    })}
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   SUMMARY PANEL — Now in BookingSummary.tsx
   ═══════════════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE SUMMARY BAR
   ═══════════════════════════════════════════════════════════════════════════ */
const MobileSummaryBar: React.FC<{ totals: Totals; expanded: boolean; onToggle: () => void }> = ({ totals, expanded, onToggle }) => (
  <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100, bgcolor: T.white, borderTop: `1px solid ${T.line}`, boxShadow: "0 -2px 12px rgba(0,0,0,0.06)" }}>
    {expanded && (
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        {[{ l: "Hall", v: totals.hallSubtotal }, { l: "Menu", v: totals.foodSubtotal }, { l: "Bar", v: totals.barSubtotal }, { l: "Add-ons", v: totals.addonsSubtotal }, ...(totals.vendorServicesSubtotal > 0 ? [{ l: "Vendors", v: totals.vendorServicesSubtotal }] : []), ...(totals.ledSubtotal > 0 ? [{ l: "LED", v: totals.ledSubtotal }] : []), ...(totals.discountApplied > 0 ? [{ l: "Discount", v: -totals.discountApplied }] : []), { l: "HST", v: totals.hstAmount }].map((r, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: "3px" }}>
            <Typography sx={{ fontSize: 12, color: T.slateLt }}>{r.l}</Typography>
            <Typography sx={{ fontSize: 12, color: T.navy }}>{fmtCAD(r.v)}</Typography>
          </Box>
        ))}
        <Box sx={{ height: 1, bgcolor: T.line, my: 0.5 }} />
      </Box>
    )}
    <Box onClick={onToggle} sx={{ px: 3, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
      <Box>
        <Typography sx={{ fontSize: 10, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em" }}>Total</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.navy, lineHeight: 1.1 }}>{fmtCAD(totals.grandTotal)}</Typography>
      </Box>
      <Typography sx={{ fontSize: 10, color: T.slateLt }}>{expanded ? "Hide" : "Details"}</Typography>
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const BanquetBookingCreate: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showMessage, showErrorMessage } = useNotifier();

  const initialForm = useMemo(() => {
    const f = { ...INITIAL_FORM };
    const d = searchParams.get("date"); const s = searchParams.get("slot");
    if (d) f.eventDate = d;
    if (s && ["AM", "PM", "FULL_DAY"].includes(s)) {
      f.timeSlot = s;
      const slotDef = TIME_SLOTS.find(ts => ts.value === s);
      if (slotDef) { f.startTime = slotDef.startTime; f.endTime = slotDef.endTime; }
    }
    return f;
  }, [searchParams]);

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<BookingFormState>(initialForm);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);
  const [saveBooking, { isLoading: isSaving }] = useSaveBanquetBookingMutation();

  const totals = useMemo(() => calculateTotal(form), [form]);
  const currentVal = useMemo(() => validators[activeStep](form), [form, activeStep]);
  const stepStatus = useMemo(() => STEPS.map((_, i) => i < activeStep ? "done" as const : i === activeStep ? "current" as const : "pending" as const), [activeStep]);

  const handleNext = () => {
    const v = validators[activeStep](form);
    if (!v.valid) { showErrorMessage(v.errors[0]); return; }
    // Sync expectedGuests → guestCount when moving from Step 1 to Step 2
    if (activeStep === 0 && form.expectedGuests) {
      setForm(p => ({ ...p, guestCount: p.expectedGuests }));
    }
    setActiveStep(s => Math.min(s + 1, 2));
  };
  const handleBack = () => setActiveStep(s => Math.max(s - 1, 0));
  const handleStepClick = (step: number) => {
    if (step < activeStep) { setActiveStep(step); return; }
    for (let i = activeStep; i < step; i++) { const v = validators[i](form); if (!v.valid) { showErrorMessage(v.errors[0]); setActiveStep(i); return; } }
    setActiveStep(step);
  };

  const handleSubmit = async (status: "quote" | "order") => {
    const e1 = validateStep1(form); const e2 = validateStep2(form);
    if (!e1.valid) { showErrorMessage(e1.errors[0]); setActiveStep(0); return; }
    if (!e2.valid) { showErrorMessage(e2.errors[0]); setActiveStep(1); return; }
    const payload = {
      banquetName: form.banquetName, agentName: form.agentName, eventType: form.eventType,
      organizationName: form.primaryCustomer.organization || undefined,
      eventDate: form.eventDate, timeSlot: form.timeSlot, status,
      timeSlotLabel: `${form.startTime} – ${form.endTime}`,
      startTime: form.startTime, endTime: form.endTime,
      expectedGuests: n(form.expectedGuests), guestCount: n(form.guestCount), childrenCount: n(form.childrenCount), guestMinimum: n(form.guestMinimum),
      barPatrons: n(form.barPatrons), menuOption: form.menuOption, menuPricePerPerson: n(form.menuPricePerPerson),
      foodServiceOption: form.foodServiceOption, foodServicePricePerPerson: n(form.foodServicePricePerPerson),
      barServiceOption: form.barServiceOption, barServiceAmount: n(form.barServiceAmount), barServiceType: form.barServiceType,
      bartenderCount: n(form.bartenderCount), bartenderAmount: n(form.bartenderAmount),
      barPackageOption: form.barPackageOption, barPackagePricePerPerson: n(form.barPackagePricePerPerson),
      lcboLicence: form.lcboLicence, barHours: n(form.barHours),
      hallFlatFee: n(form.hallFlatFee), hallPricePerPerson: n(form.hallPricePerPerson),
      hallSubtotal: totals.hallSubtotal, foodSubtotal: totals.foodSubtotal, barSubtotal: totals.barSubtotal,
      addonsSubtotal: totals.addonsSubtotal, vendorServicesSubtotal: totals.vendorServicesSubtotal, ledSubtotal: totals.ledSubtotal,
      selectedLEDs: form.selectedLEDs,
      subtotal: totals.subtotal, adjustment: totals.adjustment,
      adjustmentReason: form.adjustmentReason, serviceChargePercent: n(form.serviceChargePercent),
      serviceChargeAmount: totals.serviceChargeAmount, gratuityPercent: n(form.gratuityPercent),
      gratuityAmount: totals.gratuityAmount, hstAmount: totals.hstAmount, grandTotal: totals.grandTotal,
      securityDepositAmount: n(form.securityDepositAmount), securityDepositMethod: form.securityDepositMethod,
      securityDepositDate: form.securityDepositDate || undefined,
      publicNotes: form.publicNotes, privateNotes: form.privateNotes,
      inhouseSnapshot: {
        foodServiceOption: form.foodServiceOption, menuOption: form.menuOption,
        menuPricePerPerson: n(form.menuPricePerPerson), foodServicePricePerPerson: n(form.foodServicePricePerPerson),
        barServiceOption: form.barServiceOption, barPackageOption: form.barPackageOption,
        bartenderCount: n(form.bartenderCount), bartenderAmount: n(form.bartenderAmount),
        barHours: n(form.barHours), selectedLEDs: form.selectedLEDs,
        addons: form.addons,
      },
      vendorSnapshot: form.vendorServices.map(vs => ({
        itemId: vs.itemId, itemName: vs.itemName, vendorId: vs.vendorId, vendorName: vs.vendorName,
        serviceName: vs.serviceName, categoryName: vs.categoryName,
        pricingType: vs.pricingType, rate: vs.rate, quantity: vs.quantity, hours: vs.hours, finalPrice: vs.finalPrice,
      })),
      halls: form.halls,
      primaryCustomer: { ...form.primaryCustomer, customerType: "primary" },
      secondaryCustomer: form.hasSecondaryCustomer ? { ...form.secondaryCustomer, customerType: "secondary" } : undefined,
      payments: form.payments.filter(p => n(p.amount) > 0).map(p => ({ amount: n(p.amount), method: p.method, paymentDate: p.paymentDate || undefined })),
      addons: form.addons.map(a => ({ service_id: a.service_id, itemName: a.itemName, category: a.category, quantity: a.quantity, unitPrice: a.unitPrice })),
      vendorServices: form.vendorServices.map(vs => ({ service_id: vs.serviceId, vendor_id: vs.vendorId, quantity: vs.quantity, hours: vs.hours, final_price: vs.finalPrice })),
      vendorItems: form.vendorServices.map(vs => ({
        item_id: vs.itemId,
        vendor_id: vs.vendorId,
        item_name_snapshot: vs.itemName,
        vendor_name_snapshot: vs.vendorName,
        pricing_type_snapshot: vs.pricingType,
        base_price_snapshot: vs.basePrice,
        final_price_snapshot: vs.finalPrice,
        is_adjustable: vs.isAdjustable,
        min_price_limit: vs.minPriceLimit,
        is_default_vendor: vs.isDefaultVendor,
        service_name_snapshot: vs.serviceName,
        category_name_snapshot: vs.categoryName,
        subcategory_name_snapshot: vs.subcategoryName,
        pricing_type: vs.pricingType,
        rate: vs.rate,
        quantity: vs.quantity,
        hours: vs.hours,
        base_price: vs.basePrice,
        discount: Math.max(0, vs.basePrice - vs.finalPrice),
        final_price: vs.finalPrice,
      })),
    };
    try {
      const res = (await (saveBooking as any)(payload).unwrap()) as any;
      if (res.status) { showMessage(res.message ?? "Booking created"); navigate(`/banquet/booking/${res.data?.id ?? ""}`); }
      else showErrorMessage(res.message ?? "Failed");
    } catch (err: any) { showErrorMessage(err?.data?.message ?? err?.message ?? "Failed"); }
  };

  const btnBase = { textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px", boxShadow: "none", transition: "all 0.15s" } as const;

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 } }}>
      <Box sx={{ pb: { xs: 16, sm: 6 } }}>

        {/* ── Header ── */}
        <Box sx={{ mb: { xs: 2, md: 3 }, pt: 1 }}>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            New Booking
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.5 }}>
            {STEPS[activeStep].label} &middot; Step {activeStep + 1} of {STEPS.length}
          </Typography>
        </Box>

        {/* ── Stepper ── */}
        <Stepper active={activeStep} onClick={handleStepClick} status={stepStatus} xs={isXs} />

        {/* ── Layout: Form + Estimate ── */}
        <Box sx={{ display: "flex", gap: { md: 4, lg: 5 }, alignItems: "flex-start" }}>

          {/* LEFT — Form */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {activeStep === 0 && <Step1EventDetails form={form} setForm={setForm} />}
            {activeStep === 1 && <Step2Services form={form} setForm={setForm} />}
            {activeStep === 2 && <Step3Addons form={form} setForm={setForm} />}

            {/* Actions */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, mb: 2, gap: 1, flexWrap: "wrap" }}>
              <Button
                startIcon={<ChevronLeftIcon />}
                onClick={activeStep === 0 ? () => navigate("/banquet/calendar") : handleBack}
                disabled={isSaving}
                sx={{ ...btnBase, color: T.slate, "&:hover": { bgcolor: T.bg } }}
              >
                {activeStep === 0 ? "Cancel" : "Back"}
              </Button>

              <Box sx={{ display: "flex", gap: 1.5 }}>
                {activeStep < 2 ? (
                  <Button variant="contained" endIcon={<ChevronRightIcon />} onClick={handleNext} disabled={!currentVal.valid}
                    sx={{ ...btnBase, bgcolor: T.navy, color: T.white, px: 4, "&:hover": { bgcolor: T.navyMid }, "&.Mui-disabled": { bgcolor: T.line, color: T.slateLt } }}>
                    Continue
                  </Button>
                ) : (
                  <>
                    <Button variant="outlined" onClick={() => handleSubmit("quote")} disabled={isSaving}
                      startIcon={isSaving ? <CircularProgress size={14} /> : undefined}
                      sx={{ ...btnBase, borderColor: T.line, color: T.navy, "&:hover": { borderColor: T.gold, bgcolor: T.peach } }}>
                      Save Quote
                    </Button>
                    <Button variant="contained" onClick={() => handleSubmit("order")} disabled={isSaving}
                      startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
                      sx={{ ...btnBase, bgcolor: T.navy, color: T.white, px: 4, "&:hover": { bgcolor: T.navyMid } }}>
                      Confirm Order
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* RIGHT — Estimate */}
          {!isMobile && (
            <Box sx={{ width: 340, flexShrink: 0, alignSelf: "flex-start", position: "sticky", top: 20 }}>
              <BookingSummary totals={totals} form={form} />
            </Box>
          )}
        </Box>

        {isMobile && <MobileSummaryBar totals={totals} expanded={mobileSummaryOpen} onToggle={() => setMobileSummaryOpen(v => !v)} />}
      </Box>
    </Container>
  );
};

export default BanquetBookingCreate;
